import { exportDocument, exportProDocument, type LoadedDocument } from './session';
import { downloadBytes, downloadText } from './download';
import { getTextFormat, type ParseWarning, type TextFormatId } from '../../core';
import { getProFormat, PRO_FORMAT_IDS, type ProFormatId } from '../../core/formats/pro';

/** A conversion/export target: a Free text format or a Pro format. */
export type TargetId = TextFormatId | ProFormatId;

const PRO_IDS = PRO_FORMAT_IDS as readonly ProFormatId[];

/** Whether a target is one of the Pro formats (license-gated at export). */
export function isProTarget(id: TargetId): id is ProFormatId {
  return (PRO_IDS as readonly string[]).includes(id);
}

/** Human name for a target format id. */
export function targetName(id: TargetId): string {
  return isProTarget(id) ? getProFormat(id).name : getTextFormat(id).name;
}

/**
 * Serialize the loaded document to `target` and trigger a browser download,
 * returning any capability-drop warnings plus the exact `filename` the download
 * carried — so success confirmations (cue-forge-w28.13) can name the real file
 * instead of re-deriving it and risking drift. Shared by the Converter and the
 * Studio in-place Export (cue-forge-6f7) so both take the identical export path.
 * Throws CharsetUnsupportedError when a Pro codec cannot represent the text —
 * the caller surfaces it instead of downloading a garbage file.
 */
export async function exportAndDownload(
  loaded: LoadedDocument,
  target: TargetId,
): Promise<{ warnings: ParseWarning[]; filename: string }> {
  if (isProTarget(target)) {
    const out = await exportProDocument(loaded, target);
    if (out.kind === 'binary') downloadBytes(out.filename, out.bytes!);
    else downloadText(out.filename, out.text!, 'text/plain');
    return { warnings: out.warnings, filename: out.filename };
  }
  const out = exportDocument(loaded, target);
  downloadText(out.filename, out.text, 'text/plain');
  return { warnings: out.warnings, filename: out.filename };
}
